// Staffing drawer for one company assignment — modelled on
// OptionalTaskExercisesDrawer: a drawer that fetches its own children and
// invalidates both its own query key and the parent company list on every
// write (via programCompanyStaffingLineHooks' extraInvalidateKeys), so the
// parent row's FTE/Vacancies/Workshare re-derive with no reload.
//
// A staffing line with no contact IS a vacancy — there is deliberately no
// separate "filled" boolean (major-push/7-29-specra/09-subcontractor-fte-drilldown.md).
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Timestamp } from '@bufbuild/protobuf';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, Label, Popover, PopoverContent, PopoverTrigger, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraBadge, SpecraButton, SpecraIconButton, SpecraInput, SpecraOverlayDrawer, SpecraSectionHeader, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ChevronsUpDown, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useProgramContext } from '../../../../layouts/ProgramLayout';
import { withStoredFallback } from '../../_shared/api/selectOptions';
import { DrawerDocumentsSection } from '../../_shared/components/DrawerDocumentsSection';
import { useTenantId } from '../../_shared/hooks/useTenantId';
import { contactHooks, laborCategoryHooks } from '../../Registries/hooks';
import { programCompanyStaffingLineHooks } from '../relationshipHooks';
// Radix Select forbids an empty-string item value — "no contact" is modeled
// as this sentinel and mapped to '' on submit (same pattern as EntityTable's
// ALL_FILTER).
const VACANT = '__vacant__';
const EMPTY_LINE = {
    lcatTitle: '',
    lcatId: '',
    positionTitle: '',
    fte: '',
    contactId: VACANT,
    startDate: '',
    endDate: '',
    notes: ''
};
const dateStr = (ts)=>ts ? ts.toDate().toISOString().slice(0, 10) : '—';
const dateInputValue = (ts)=>ts ? ts.toDate().toISOString().slice(0, 10) : '';
const toTimestamp = (ymd)=>ymd ? Timestamp.fromDate(new Date(`${ymd}T00:00:00Z`)) : undefined;
export function CompanyStaffingDrawer({ programId, assignment, onClose }) {
    const { canEdit } = useProgramContext();
    const tenantId = useTenantId();
    const { data: linesData, isLoading } = programCompanyStaffingLineHooks.useList({
        programId,
        companyAssignmentId: assignment.id
    });
    const lines = linesData?.items ?? [];
    const upsertLine = programCompanyStaffingLineHooks.useUpsert();
    const removeLine = programCompanyStaffingLineHooks.useDelete();
    const contactsQuery = contactHooks.useList({
        companyId: tenantId
    });
    const contacts = contactsQuery.data?.items ?? [];
    const contactById = useMemo(()=>{
        const m = new Map();
        for (const c of contacts)m.set(c.id, c);
        return m;
    }, [
        contacts
    ]);
    // The Contact picker only offers contacts ASSOCIATED WITH THIS COMPANY in
    // the registry ("if I'm linking an LCAT to Oddball, only Oddball contacts
    // should be available") — filtered client-side from the tenant contact
    // list the drawer already loads. An assignment with no companyProfileId
    // has no company to filter by, so it offers none (plus whatever the form
    // already holds via the stored-fallback below).
    const companyContacts = useMemo(()=>assignment.companyProfileId ? contacts.filter((c)=>c.companyProfileId === assignment.companyProfileId) : [], [
        contacts,
        assignment.companyProfileId
    ]);
    const contactOptions = useMemo(()=>companyContacts.map((c)=>({
                value: c.id,
                label: `${c.firstName} ${c.lastName}`.trim() || c.email
            })), [
        companyContacts
    ]);
    // People (spec 10 recommendation (c)): the union of two truths that are
    // allowed to differ — contacts STAFFED on this assignment's lines (the
    // program's answer, regardless of who employs them) and contacts
    // AFFILIATED with this company (employer-of-record) with no line here.
    // One row per person even across multiple lines; a vacancy (null contact)
    // is never a person.
    const people = useMemo(()=>{
        const byId = new Map();
        const now = Date.now();
        for (const line of lines){
            if (!line.contactId) continue;
            const c = contactById.get(line.contactId);
            const name = line.contactName || (c ? `${c.firstName} ${c.lastName}`.trim() : 'Unknown contact');
            const row = byId.get(line.contactId) ?? {
                contactId: line.contactId,
                name,
                lines: []
            };
            // A past end date means "was staffed, no longer is" — spec 10: show
            // past lines as past, never as live headcount.
            const ended = line.endDate && line.endDate.toDate().getTime() < now;
            row.lines.push({
                id: line.id,
                label: line.lcatTitle || line.positionTitle || 'Unspecified role',
                fte: line.fte,
                endedOn: ended ? dateStr(line.endDate) : ''
            });
            byId.set(line.contactId, row);
        }
        if (assignment.companyProfileId) {
            for (const c of contacts){
                if (c.companyProfileId === assignment.companyProfileId && !byId.has(c.id)) {
                    byId.set(c.id, {
                        contactId: c.id,
                        name: `${c.firstName} ${c.lastName}`.trim() || c.email || 'Unnamed contact',
                        lines: []
                    });
                }
            }
        }
        return [
            ...byId.values()
        ].sort((a, b)=>b.lines.length - a.lines.length || a.name.localeCompare(b.name));
    }, [
        lines,
        contacts,
        contactById,
        assignment.companyProfileId
    ]);
    // W2-A4 (major-push/lcat-intelligence): the picker reads the TENANT
    // REGISTRY (current LCAT versions), not zou's per-project extraction rows —
    // a picked title persists the registry entry id into lcat_id, which is what
    // the column has meant since #973. Free text remains first-class: an
    // unmatched title saves with an empty lcat_id and can be mapped later (or
    // never). Zou extractions reach this list through the registry's candidate
    // review queue, not directly.
    const registryQuery = laborCategoryHooks.useList({
        companyId: tenantId
    });
    const lcatOptions = useMemo(()=>(registryQuery.data?.items ?? []).map((e)=>({
                id: e.id,
                title: e.title,
                companyProfileId: e.companyProfileId,
                companyName: e.companyName
            })), [
        registryQuery.data
    ]);
    const createEntry = laborCategoryHooks.useUpsert();
    const [lcatOpen, setLcatOpen] = useState(false);
    const [lcatSearch, setLcatSearch] = useState('');
    const [form, setForm] = useState(EMPTY_LINE);
    const [editingId, setEditingId] = useState(null);
    // The add/edit form is hidden by default (spec: clicking a row's Edit used
    // to just repopulate a form mounted far below with no visible state
    // change) — it only mounts via "Add New Staff" or a row's Edit action, and
    // closes itself on save or Cancel.
    const [formOpen, setFormOpen] = useState(false);
    // A line already holding a contact outside this company (legacy data, or a
    // contact later re-affiliated elsewhere) must keep showing that contact so
    // opening Edit on it can't silently blank the field — flagged as unusual
    // (same "stored fallback" idiom as IssuesTab's superseded-CLIN picker),
    // not presented as a normal in-company choice.
    const contactSelectOptions = withStoredFallback(contactOptions, form.contactId === VACANT ? '' : form.contactId);
    const setField = (key, value)=>setForm((prev)=>({
                ...prev,
                [key]: value
            }));
    const resetForm = ()=>{
        setForm(EMPTY_LINE);
        setEditingId(null);
    };
    const closeForm = ()=>{
        resetForm();
        setFormOpen(false);
    };
    const openAddForm = ()=>{
        resetForm();
        setFormOpen(true);
    };
    const startEdit = (line)=>{
        setEditingId(line.id);
        setForm({
            lcatTitle: line.lcatTitle,
            lcatId: line.lcatId,
            positionTitle: line.positionTitle,
            fte: line.fte ? String(line.fte) : '',
            contactId: line.contactId || VACANT,
            startDate: dateInputValue(line.startDate),
            endDate: dateInputValue(line.endDate),
            notes: line.notes
        });
        setFormOpen(true);
    };
    // Combobox actions (Phase 2b slice 2): an explicit pick carries its own
    // entry id (no title-matching ambiguity); free text stays first-class with
    // an empty id; and Create mints a registry entry inline — a shared-catalog
    // write, so the server's area:registries gate is the authority (a persona
    // without the bundle gets the clean deny toast, and free text remains).
    const pickLcat = (o)=>{
        setForm((prev)=>({
                ...prev,
                lcatTitle: o.title,
                lcatId: o.id
            }));
        setLcatOpen(false);
        setLcatSearch('');
    };
    const useFreeText = ()=>{
        setForm((prev)=>({
                ...prev,
                lcatTitle: lcatSearch,
                lcatId: ''
            }));
        setLcatOpen(false);
        setLcatSearch('');
    };
    const createLcat = async ()=>{
        const created = await createEntry.mutateAsync({
            id: '',
            companyId: tenantId,
            // New-from-staffing defaults to this assignment's company (product
            // call, 2026-08-10) — the seat being staffed is that company's seat.
            companyProfileId: assignment.companyProfileId ?? '',
            contractVehicleId: '',
            title: lcatSearch.trim(),
            description: '',
            qualifications: '',
            clearance: '',
            certifications: ''
        });
        if (created) {
            pickLcat({
                id: created.id,
                title: created.title
            });
        }
    };
    const handleSave = async ()=>{
        await upsertLine.mutateAsync({
            id: editingId ?? '',
            programId,
            companyAssignmentId: assignment.id,
            // The registry entry id when the title was picked from the catalog;
            // empty for free text (first-class — an unmapped seat is legal forever).
            lcatId: form.lcatId,
            lcatTitle: form.lcatTitle.trim(),
            positionTitle: form.positionTitle.trim(),
            fte: Number(form.fte) || 0,
            contactId: form.contactId === VACANT ? '' : form.contactId,
            startDate: toTimestamp(form.startDate),
            endDate: toTimestamp(form.endDate),
            notes: form.notes
        });
        // Closes on save — both create and update — so the drawer never leaves a
        // stale form mounted after a successful write.
        closeForm();
    };
    // Placeholder-from-vacancy (Phase 3 slice 2, the gap-analysis nugget): an
    // EXPLICIT click mints a seat-shaped contact — named for the position,
    // flagged is_placeholder, affiliated with this company — and fills the
    // line with it. Never automatic: the registry only grows when you say so.
    const createPlaceholder = contactHooks.useUpsert();
    const handleReservePlaceholder = async (line)=>{
        const seatName = line.lcatTitle || line.positionTitle || 'Open seat';
        const created = await createPlaceholder.mutateAsync({
            id: '',
            companyId: tenantId,
            firstName: seatName,
            lastName: '(Placeholder)',
            title: seatName,
            phone: '',
            location: '',
            organization: '',
            contactType: 'EXTERNAL',
            companyProfileId: assignment.companyProfileId,
            governmentOrganizationId: '',
            isActive: true,
            isPlaceholder: true
        });
        if (!created?.id) return;
        await upsertLine.mutateAsync({
            id: line.id,
            programId,
            companyAssignmentId: assignment.id,
            lcatId: line.lcatId,
            lcatTitle: line.lcatTitle,
            positionTitle: line.positionTitle,
            fte: line.fte,
            contactId: created.id,
            startDate: line.startDate,
            endDate: line.endDate,
            notes: line.notes
        });
    };
    const handleDelete = async (id)=>{
        await removeLine.mutateAsync({
            id,
            programId
        });
        if (editingId === id) closeForm();
    };
    return /*#__PURE__*/ _jsxs(SpecraOverlayDrawer, {
        open: true,
        onOpenChange: (next)=>{
            if (!next) onClose();
        },
        title: `${assignment.companyName || 'Company'} — Staffing`,
        testId: Testids.deliveryProgramRel.companyStaffingDrawer,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-1 text-sm",
                "data-testid": Testids.deliveryProgramRel.companyStaffingSummary,
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex justify-between",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-muted-foreground",
                                children: "FTE"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-medium",
                                children: assignment.fteCount
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex justify-between",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-muted-foreground",
                                children: "Vacancies"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                children: assignment.vacancies
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mt-[var(--space-5-5)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        variant: "group",
                        title: "Staffing lines",
                        className: "mb-2",
                        action: canEdit && !formOpen && /*#__PURE__*/ _jsxs(SpecraButton, {
                            size: "sm",
                            variant: "secondary",
                            onClick: openAddForm,
                            "data-testid": Testids.deliveryProgramRel.companyStaffingAddNew,
                            children: [
                                /*#__PURE__*/ _jsx(Plus, {
                                    className: "mr-1 h-3.5 w-3.5"
                                }),
                                "Add New Staff"
                            ]
                        })
                    }),
                    isLoading && /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "Loading…"
                    }),
                    !isLoading && lines.length === 0 && /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "No staffing lines yet. FTE and Vacancies above are the hand-typed values until the first line is added."
                    }),
                    /*#__PURE__*/ _jsx("ul", {
                        className: "space-y-2",
                        children: lines.map((line)=>/*#__PURE__*/ _jsxs("li", {
                                className: `rounded-md border p-2 text-sm ${line.id === editingId ? 'border-primary bg-accent/40' : ''}`,
                                "data-testid": Testids.deliveryProgramRel.companyStaffingRow,
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center justify-between gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "font-medium",
                                                children: line.lcatTitle || line.positionTitle || '—'
                                            }),
                                            /*#__PURE__*/ _jsxs("span", {
                                                children: [
                                                    line.fte,
                                                    " FTE"
                                                ]
                                            })
                                        ]
                                    }),
                                    line.positionTitle && line.lcatTitle && /*#__PURE__*/ _jsx("div", {
                                        className: "text-muted-foreground",
                                        children: line.positionTitle
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center justify-between gap-2 text-muted-foreground",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                children: line.contactId ? line.contactName || (()=>{
                                                    const c = contactById.get(line.contactId);
                                                    return c ? `${c.firstName} ${c.lastName}`.trim() : 'Assigned';
                                                })() : 'Vacancy'
                                            }),
                                            canEdit && !line.contactId && /*#__PURE__*/ _jsx(SpecraButton, {
                                                size: "sm",
                                                variant: "ghost",
                                                disabled: createPlaceholder.isPending || upsertLine.isPending,
                                                "data-testid": Testids.deliveryProgramRel.companyStaffingPlaceholderCreate,
                                                onClick: ()=>handleReservePlaceholder(line),
                                                children: "Reserve placeholder"
                                            })
                                        ]
                                    }),
                                    (line.startDate || line.endDate) && /*#__PURE__*/ _jsxs("div", {
                                        className: "text-muted-foreground",
                                        children: [
                                            dateStr(line.startDate),
                                            " – ",
                                            dateStr(line.endDate)
                                        ]
                                    }),
                                    line.notes && /*#__PURE__*/ _jsx("div", {
                                        className: "text-muted-foreground",
                                        children: line.notes
                                    }),
                                    canEdit && /*#__PURE__*/ _jsxs("div", {
                                        className: "mt-1 flex justify-end gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                size: "sm",
                                                variant: "ghost",
                                                onClick: ()=>startEdit(line),
                                                "data-testid": Testids.deliveryProgramRel.companyStaffingEdit,
                                                children: "Edit"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraIconButton, {
                                                size: "sm",
                                                variant: "ghost",
                                                label: "Delete staffing line",
                                                "data-testid": Testids.deliveryProgramRel.companyStaffingDelete,
                                                onClick: ()=>handleDelete(line.id),
                                                children: /*#__PURE__*/ _jsx(Trash2, {
                                                    className: "h-3.5 w-3.5"
                                                })
                                            })
                                        ]
                                    })
                                ]
                            }, line.id))
                    })
                ]
            }),
            canEdit && formOpen && /*#__PURE__*/ _jsxs("div", {
                className: "mt-[var(--space-5-5)] space-y-3",
                "data-testid": Testids.deliveryProgramRel.companyStaffingLineForm,
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        variant: "group",
                        title: editingId ? `Edit staffing line${form.lcatTitle || form.positionTitle ? ` — ${form.lcatTitle || form.positionTitle}` : ''}` : 'Add staffing line'
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx(Label, {
                                htmlFor: "line-lcat",
                                children: "Labor category"
                            }),
                            /*#__PURE__*/ _jsxs(Popover, {
                                open: lcatOpen,
                                onOpenChange: setLcatOpen,
                                children: [
                                    /*#__PURE__*/ _jsx(PopoverTrigger, {
                                        asChild: true,
                                        children: /*#__PURE__*/ _jsxs(SpecraButton, {
                                            id: "line-lcat",
                                            type: "button",
                                            variant: "secondary",
                                            role: "combobox",
                                            "aria-expanded": lcatOpen,
                                            className: "h-10 w-full justify-between bg-background font-normal",
                                            "data-testid": Testids.deliveryProgramRel.companyStaffingLcatInput,
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "truncate text-left",
                                                    children: form.lcatTitle || 'Search or type a labor category…'
                                                }),
                                                /*#__PURE__*/ _jsx(ChevronsUpDown, {
                                                    className: "ml-2 h-4 w-4 shrink-0 opacity-50"
                                                })
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(PopoverContent, {
                                        className: "w-[var(--radix-popover-trigger-width)] p-0",
                                        align: "start",
                                        children: /*#__PURE__*/ _jsxs(Command, {
                                            children: [
                                                /*#__PURE__*/ _jsx(CommandInput, {
                                                    placeholder: "Search the registry or type freely…",
                                                    value: lcatSearch,
                                                    onValueChange: setLcatSearch
                                                }),
                                                /*#__PURE__*/ _jsxs(CommandList, {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(CommandEmpty, {
                                                            className: "py-3 text-center text-xs",
                                                            children: "No registry match — use the actions below."
                                                        }),
                                                        /*#__PURE__*/ _jsx(CommandGroup, {
                                                            children: lcatOptions.map((o)=>/*#__PURE__*/ _jsxs(CommandItem, {
                                                                    value: `${o.title} ${o.companyName} ${o.id}`,
                                                                    onSelect: ()=>pickLcat(o),
                                                                    className: "flex flex-col items-start gap-0.5 py-2",
                                                                    children: [
                                                                        /*#__PURE__*/ _jsx("span", {
                                                                            className: "font-medium",
                                                                            children: o.title
                                                                        }),
                                                                        /*#__PURE__*/ _jsx("span", {
                                                                            className: "text-[length:var(--fs-ui-eyebrow)] text-muted-foreground",
                                                                            children: o.companyName || 'No company (as written)'
                                                                        })
                                                                    ]
                                                                }, o.id))
                                                        }),
                                                        lcatSearch.trim() !== '' && /*#__PURE__*/ _jsxs(CommandGroup, {
                                                            forceMount: true,
                                                            heading: "Not in the catalog?",
                                                            children: [
                                                                /*#__PURE__*/ _jsxs(CommandItem, {
                                                                    forceMount: true,
                                                                    value: `__free__${lcatSearch}`,
                                                                    onSelect: useFreeText,
                                                                    children: [
                                                                        "Use “",
                                                                        lcatSearch,
                                                                        "” as free text"
                                                                    ]
                                                                }),
                                                                canEdit && /*#__PURE__*/ _jsxs(CommandItem, {
                                                                    forceMount: true,
                                                                    value: `__create__${lcatSearch}`,
                                                                    onSelect: ()=>void createLcat(),
                                                                    disabled: createEntry.isPending,
                                                                    "data-testid": Testids.deliveryProgramRel.companyStaffingLcatCreate,
                                                                    children: [
                                                                        /*#__PURE__*/ _jsx(Plus, {
                                                                            className: "mr-1 h-3.5 w-3.5"
                                                                        }),
                                                                        "Create “",
                                                                        lcatSearch,
                                                                        "” in the registry"
                                                                    ]
                                                                })
                                                            ]
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx(Label, {
                                htmlFor: "line-position",
                                children: "Position title"
                            }),
                            /*#__PURE__*/ _jsx(SpecraInput, {
                                id: "line-position",
                                value: form.positionTitle,
                                onChange: (e)=>setField('positionTitle', e.target.value),
                                placeholder: "e.g. Senior Systems Engineer, Site B"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "grid grid-cols-1 gap-3 sm:grid-cols-2",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx(Label, {
                                        htmlFor: "line-fte",
                                        children: "FTE"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        id: "line-fte",
                                        type: "number",
                                        min: "0",
                                        step: "0.1",
                                        value: form.fte,
                                        onChange: (e)=>setField('fte', e.target.value)
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx(Label, {
                                        htmlFor: "line-contact",
                                        children: "Contact"
                                    }),
                                    /*#__PURE__*/ _jsxs(Select, {
                                        value: form.contactId,
                                        onValueChange: (v)=>setField('contactId', v),
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                                id: "line-contact",
                                                children: /*#__PURE__*/ _jsx(SelectValue, {
                                                    placeholder: "Vacancy"
                                                })
                                            }),
                                            /*#__PURE__*/ _jsxs(SelectContent, {
                                                children: [
                                                    /*#__PURE__*/ _jsx(SelectItem, {
                                                        value: VACANT,
                                                        children: "Vacancy (no contact)"
                                                    }),
                                                    contactSelectOptions.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                            value: o.value,
                                                            children: o.label
                                                        }, o.value))
                                                ]
                                            })
                                        ]
                                    }),
                                    contactSelectOptions.length === 0 && /*#__PURE__*/ _jsx("p", {
                                        className: "text-xs text-muted-foreground",
                                        children: "No contacts affiliated with this company yet."
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "grid grid-cols-1 gap-3 sm:grid-cols-2",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx(Label, {
                                        htmlFor: "line-start",
                                        children: "Start date"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        id: "line-start",
                                        type: "date",
                                        value: form.startDate,
                                        onChange: (e)=>setField('startDate', e.target.value)
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx(Label, {
                                        htmlFor: "line-end",
                                        children: "End date"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        id: "line-end",
                                        type: "date",
                                        value: form.endDate,
                                        onChange: (e)=>setField('endDate', e.target.value)
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx(Label, {
                                htmlFor: "line-notes",
                                children: "Notes"
                            }),
                            /*#__PURE__*/ _jsx(SpecraTextarea, {
                                id: "line-notes",
                                rows: 2,
                                value: form.notes,
                                onChange: (e)=>setField('notes', e.target.value)
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                onClick: ()=>{
                                    handleSave().catch((err)=>toast.error(err instanceof Error ? err.message : 'Failed to save staffing line'));
                                },
                                disabled: upsertLine.isPending || !form.fte,
                                "data-testid": Testids.deliveryProgramRel.companyStaffingCreate,
                                children: upsertLine.isPending ? 'Saving…' : editingId ? 'Update line' : 'Add line'
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "ghost",
                                onClick: closeForm,
                                children: "Cancel"
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mt-[var(--space-5-5)]",
                "data-testid": Testids.deliveryProgramRel.companyStaffingPeopleSection,
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        variant: "group",
                        title: "People",
                        className: "mb-2"
                    }),
                    !assignment.active && /*#__PURE__*/ _jsx("p", {
                        className: "mb-2 text-xs text-muted-foreground",
                        children: "This company assignment is no longer active — the people below are historical affiliation and staffing, not current headcount."
                    }),
                    people.length === 0 ? /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "No people yet — affiliate contacts with this company in Registries, or assign one to a staffing line above."
                    }) : /*#__PURE__*/ _jsx("ul", {
                        className: "space-y-2",
                        children: people.map((p)=>/*#__PURE__*/ _jsxs("li", {
                                className: "rounded-md border p-2 text-sm",
                                "data-testid": Testids.deliveryProgramRel.companyStaffingPersonRow,
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center justify-between gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "font-medium",
                                                children: p.name
                                            }),
                                            /*#__PURE__*/ _jsxs("span", {
                                                className: "flex items-center gap-1",
                                                children: [
                                                    contactById.get(p.contactId)?.isPlaceholder && /*#__PURE__*/ _jsx(SpecraBadge, {
                                                        variant: "warning",
                                                        children: "Placeholder"
                                                    }),
                                                    /*#__PURE__*/ _jsx(SpecraBadge, {
                                                        variant: p.lines.length === 0 ? 'neutral' : p.lines.some((l)=>!l.endedOn) ? 'info' : 'neutral',
                                                        children: p.lines.length === 0 ? 'Affiliated' : p.lines.some((l)=>!l.endedOn) ? 'Staffed' : 'Ended'
                                                    })
                                                ]
                                            })
                                        ]
                                    }),
                                    p.lines.map((l)=>/*#__PURE__*/ _jsxs("div", {
                                            className: l.endedOn ? 'text-muted-foreground/60' : 'text-muted-foreground',
                                            children: [
                                                l.label,
                                                " · ",
                                                l.fte,
                                                " FTE",
                                                l.endedOn ? ` · Ended ${l.endedOn}` : ''
                                            ]
                                        }, l.id)),
                                    p.lines.length === 0 && /*#__PURE__*/ _jsxs("div", {
                                        className: "text-xs text-muted-foreground",
                                        children: [
                                            "Affiliated with ",
                                            assignment.companyName || 'this company',
                                            " — no position on this program."
                                        ]
                                    })
                                ]
                            }, p.contactId))
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "mt-[var(--space-5-5)]",
                children: /*#__PURE__*/ _jsx(DrawerDocumentsSection, {
                    programId: programId,
                    scope: {
                        companyAssignmentId: assignment.id
                    },
                    emptyText: "No documents recorded for this company.",
                    canEdit: canEdit
                })
            })
        ]
    });
}
